import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, FileBarChart, CheckCircle, Trash2 } from 'lucide-react';

const SubcontractClaims = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    subcontract_id: '', period_start: '', period_end: '',
    lines: [{ description: '', unit: '', quantity: 1, unit_price: 0, progress_pct: 100, amount: 0 }],
    notes: '',
  });

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        axiosInstance.get('/subcontract-claims'),
        axiosInstance.get('/subcontracts'),
      ]);
      setList(a.data || []); setContracts(b.data || []);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const recalc = (lines) => lines.map((l) => ({
    ...l, amount: +(Number(l.quantity || 0) * Number(l.unit_price || 0) * Number(l.progress_pct || 0) / 100).toFixed(2),
  }));

  const sub = form.lines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const sc = contracts.find((c) => c.id === form.subcontract_id);
  const vat = sub * ((sc?.vat_pct || 15) / 100);
  const ret = sub * ((sc?.retention_pct || 0) / 100);
  const net = sub + vat - ret;

  const save = async (e) => {
    e.preventDefault();
    if (!form.subcontract_id) return toast.error('Select a subcontract');
    try {
      await axiosInstance.post('/subcontract-claims', { ...form, lines: recalc(form.lines) });
      toast.success('Claim created'); setShow(false); load();
      setForm({ subcontract_id: '', period_start: '', period_end: '', lines: [{ description: '', unit: '', quantity: 1, unit_price: 0, progress_pct: 100, amount: 0 }], notes: '' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const approve = async (id) => {
    try {
      const r = await axiosInstance.post(`/subcontract-claims/${id}/approve`);
      toast.success(`Approved — Journal ${r.data?.journal_no}`); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Approve failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('subcontract_claims.title')}</h1>
        <button data-testid="add-claim-btn" onClick={() => setShow(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold">
          <Plus size={18} /> {t('subcontract_claims.new')}
        </button>
      </div>
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left">{t('subcontract_claims.fields.no')}</th>
              <th className="px-4 py-3 text-left">{t('subcontract_claims.fields.contract')}</th>
              <th className="px-4 py-3 text-left">{t('subcontract_claims.fields.subcontractor')}</th>
              <th className="px-4 py-3 text-left">{t('subcontract_claims.fields.project')}</th>
              <th className="px-4 py-3 text-left">{t('subcontract_claims.fields.period')}</th>
              <th className="px-4 py-3 text-left">{t('subcontract_claims.fields.total')}</th>
              <th className="px-4 py-3 text-left">{t('subcontract_claims.fields.retention')}</th>
              <th className="px-4 py-3 text-left">{t('subcontract_claims.fields.net')}</th>
              <th className="px-4 py-3 text-left">{t('common.status')}</th>
              <th className="px-4 py-3 text-left">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-xs">{c.claim_no}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.subcontract_no}</td>
                <td className="px-4 py-3">{c.subcontractor_name}</td>
                <td className="px-4 py-3">{c.project_name}</td>
                <td className="px-4 py-3 text-xs">{c.period_start} → {c.period_end}</td>
                <td className="px-4 py-3 font-mono">{(c.subtotal + c.vat).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-blue-700">{c.retention_amount?.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono font-bold">{c.net_payable?.toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${c.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'}`}>{c.approval_status}</span>
                </td>
                <td className="px-4 py-3">
                  {c.approval_status === 'pending' && (
                    <button onClick={() => approve(c.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`approve-${c.id}`}>
                      <CheckCircle size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center gap-2"><FileBarChart size={20} /><h2 className="text-xl font-bold">{t('subcontract_claims.modal.title')}</h2></div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Subcontract *</label>
                  <select required value={form.subcontract_id} onChange={(e) => setForm({ ...form, subcontract_id: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" data-testid="claim-subcontract">
                    <option value="">— Select —</option>
                    {contracts.map((c) => <option key={c.id} value={c.id}>{c.contract_no} — {c.subcontractor_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">From *</label>
                  <input type="date" required value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">To *</label>
                  <input type="date" required value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Line Items</h3>
                <table className="w-full text-xs border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left">Description</th>
                      <th className="px-2 py-2">Unit</th>
                      <th className="px-2 py-2">Qty</th>
                      <th className="px-2 py-2">Price</th>
                      <th className="px-2 py-2">Progress %</th>
                      <th className="px-2 py-2">Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((ln, i) => (
                      <tr key={i} className="border-t">
                        <td><input value={ln.description} onChange={(e) => { const ls = [...form.lines]; ls[i].description = e.target.value; setForm({ ...form, lines: recalc(ls) }); }} className="w-full px-2 py-1 text-xs" /></td>
                        <td><input value={ln.unit} onChange={(e) => { const ls = [...form.lines]; ls[i].unit = e.target.value; setForm({ ...form, lines: recalc(ls) }); }} className="w-20 px-2 py-1 text-xs text-center" /></td>
                        <td><input type="number" value={ln.quantity} onChange={(e) => { const ls = [...form.lines]; ls[i].quantity = e.target.value; setForm({ ...form, lines: recalc(ls) }); }} className="w-24 px-2 py-1 text-xs text-center font-mono" /></td>
                        <td><input type="number" value={ln.unit_price} onChange={(e) => { const ls = [...form.lines]; ls[i].unit_price = e.target.value; setForm({ ...form, lines: recalc(ls) }); }} className="w-28 px-2 py-1 text-xs text-center font-mono" /></td>
                        <td><input type="number" value={ln.progress_pct} onChange={(e) => { const ls = [...form.lines]; ls[i].progress_pct = e.target.value; setForm({ ...form, lines: recalc(ls) }); }} className="w-20 px-2 py-1 text-xs text-center font-mono" /></td>
                        <td className="px-2 py-1 text-xs text-center font-mono">{ln.amount?.toLocaleString()}</td>
                        <td>
                          <button type="button" onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })} className="text-red-600 p-1"><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button type="button" onClick={() => setForm({ ...form, lines: [...form.lines, { description: '', unit: '', quantity: 1, unit_price: 0, progress_pct: 100, amount: 0 }] })} className="mt-2 text-sm text-purple-600">+ Add Line</button>
              </div>
              <div className="bg-gray-50 rounded p-4 grid grid-cols-4 gap-4 text-sm">
                <div>Subtotal: <strong className="font-mono">{sub.toLocaleString()}</strong></div>
                <div>VAT: <strong className="font-mono text-orange-700">{vat.toFixed(2)}</strong></div>
                <div>Retention: <strong className="font-mono text-blue-700">{ret.toFixed(2)}</strong></div>
                <div>Net Payable: <strong className="font-mono text-emerald-700">{net.toFixed(2)}</strong></div>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-lg border text-sm">{t('common.cancel')}</button>
                <button type="submit" className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-sm" data-testid="save-claim-btn">{t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcontractClaims;
